/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, defer, of, timer } from 'rxjs';
import { tap, catchError, switchMap, map, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { HttpService } from '../http/http.service';
import { TokenBridgeService } from './token-bridge.service';

import type {
  ApiResponse,
  AuthContent,
  DecodedToken,
  SafeUserData,
  TokenRefreshCallback,
  UserData,
} from '@nebula-shared/domain';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly tokenBridge = inject(TokenBridgeService);

  private readonly AUTH_ENDPOINT: string = `/simappe-oauth2-server/api/v1`;

  readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  readonly isRefreshingSubject = new BehaviorSubject<boolean>(false);
  private refreshTokenQueue: TokenRefreshCallback[] = [];

  private accessTokenValue: string | null = null;
  private refreshTokenValue: string | null = null;

  private currentUser: UserData | null = null;
  private sessionId: string | null = null;
  private refreshTokenTimeout: any;

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public isRefreshing$ = this.isRefreshingSubject.asObservable();
  private isRefreshingLock = false;
  private lastRefreshTimestamp = 0;
  private readonly REFRESH_DEBOUNCE_MS = 2000;
  private readonly MAX_REFRESH_RETRIES = 3;
  private refreshFailureCount = 0;

  constructor() {
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    if (typeof window === 'undefined') return;

    const token = this.getToken();
    const expiry = this.getTokenExpiry();
    const hasValidToken = token && expiry && Date.now() < expiry - 30000;

    if (hasValidToken) {
      this.loadUserFromToken();
      this.isAuthenticatedSubject.next(true);
      this.scheduleTokenRefresh();
    } else {
      this.isAuthenticatedSubject.next(false);
    }
  }

  private loadUserFromToken(): void {
    const token = this.getToken();
    if (token) {
      const decoded = this.decodeToken(token);
      if (decoded?.User) {
        this.currentUser = decoded.User;
        this.sessionId = decoded.User.sessionId;
        this.accessTokenValue = token;
        this.refreshTokenValue = this.getRefreshToken();
      }
    }
  }

  public isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  handleSuccessfulAuth(content: AuthContent): void {
    this.accessTokenValue = content.accessToken;
    this.refreshTokenValue = content.refreshToken;
    this.sessionId = content.sessionId;
    this.refreshFailureCount = 0;

    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', content.accessToken);
      localStorage.setItem('refresh_token', content.refreshToken);

      const expiryTime = Date.now() + content.expiresIn;
      localStorage.setItem('token_expiry', expiryTime.toString());
    }

    const decoded = this.decodeToken(content.accessToken);
    if (decoded?.User) {
      this.currentUser = decoded.User;
      this.saveSafeUserDataToCookie(this.currentUser);

      const safeData = this.extractSafeUserData(this.currentUser);
      localStorage.setItem('user_data', JSON.stringify(safeData));
    }

    this.isAuthenticatedSubject.next(true);

    this.scheduleTokenRefresh();

    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('session_expired_shown');
    }
  }

  public refreshToken(): Observable<AuthContent> {
    const memoryToken = this.refreshTokenValue;
    const storageToken = localStorage.getItem('refresh_token');
    const refreshToken = memoryToken === storageToken ? memoryToken : storageToken;

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const refreshCount = parseInt(sessionStorage.getItem('refresh_count') || '0', 10) + 1;
    sessionStorage.setItem('refresh_count', refreshCount.toString());

    return this.http.post<AuthContent>(`${this.AUTH_ENDPOINT}/refresh`, { refreshToken }).pipe(
      map((response: ApiResponse<AuthContent>) => response.response.content),
      tap((content) => {
        this.handleSuccessfulAuth(content);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 500 && this.refreshFailureCount < this.MAX_REFRESH_RETRIES) {
          this.refreshFailureCount++;
          const delay = Math.min(1000 * Math.pow(2, this.refreshFailureCount), 10000);

          return timer(delay).pipe(
            switchMap(() =>
              this.http.post<AuthContent>(`${this.AUTH_ENDPOINT}/refresh`, { refreshToken }),
            ),
            map((response: ApiResponse<AuthContent>) => response.response.content),
            tap((content) => {
              this.handleSuccessfulAuth(content);
              this.refreshFailureCount = 0;
            }),
            catchError((retryError: HttpErrorResponse) => {
              if (retryError.status === 401 || retryError.status === 403) {
                this._forceLogout(true);
              }
              return throwError(() => retryError);
            }),
          );
        }

        if (error.status === 401 || error.status === 403) {
          this._forceLogout(true);
        }

        return throwError(() => error);
      }),
    );
  }

  clearNavigationData() {
    const navigationKeys = [
      'nebula:activeRoute:accounting',
      'nebula:activeRoute:budget',
      'nebula:activeRoute:tesorería',
      'nebula:activeRoute:vault',
      'nebula_ui_nav_v1',
      'selectedRoute',
      'lastUsedProductId',
    ];

    navigationKeys.forEach((key) => localStorage.removeItem(key));
  }
  private _forceLogout(showAlert = true): void {
    this.clearAllData();
    this.clearNavigationData();
    if (typeof window !== 'undefined' && this.router) {
      if (showAlert && !sessionStorage.getItem('session_expired_shown')) {
        sessionStorage.setItem('session_expired_shown', 'true');
      }

      window.location.replace('/login?sessionExpired=true&ts=' + Date.now());
    }
  }

  logoutServerSide(): Observable<void> {
    const accessToken = this.getToken();
    if (!accessToken) {
      this.clearAllData();
      return of(void 0);
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      AccessToken: accessToken,
      'Content-Type': 'application/json',
    });

    const body = { accessToken };

    return this.http
      .post<ApiResponse<void>>(`${this.AUTH_ENDPOINT}/logout`, body, undefined, headers)
      .pipe(
        map(() => void 0),
        finalize(() => {
          this.clearAllData();
        }),
        catchError(() => {
          this.clearAllData();
          return of(void 0);
        }),
      );
  }

  logout(): void {
    this.logoutServerSide()
      .pipe(
        finalize(() => {
          this.clearAllData();

          const navigationKeys = [
            'nebula:activeRoute:accounting',
            'nebula:activeRoute:budget',
            'nebula:activeRoute:tesorería',
            'nebula:activeRoute:vault',
            'nebula_ui_nav_v1',
            'selectedRoute',
            'lastUsedProductId',
          ];

          navigationKeys.forEach((key) => localStorage.removeItem(key));

          window.location.replace('/login?logout=' + Date.now());
        }),
      )
      .subscribe();
  }

  /** Clave de localStorage que marca que el usuario debe cambiar su contraseña vencida antes de entrar. */
  private static readonly MUST_CHANGE_PASSWORD_KEY = 'must_change_password';

  /**
   * Marca (o limpia) que el usuario autenticado tiene la contraseña vencida y debe cambiarla antes
   * de entrar. Lo fija el login a partir de {@link AuthContent#mustChangePassword}.
   *
   * @param pending `true` para exigir el cambio; `false` para limpiarlo.
   */
  setMustChangePassword(pending: boolean): void {
    if (typeof localStorage === 'undefined') return;
    if (pending) {
      localStorage.setItem(AuthService.MUST_CHANGE_PASSWORD_KEY, '1');
    } else {
      localStorage.removeItem(AuthService.MUST_CHANGE_PASSWORD_KEY);
    }
  }

  /** @return `true` si hay un cambio de contraseña vencida pendiente para el usuario actual. */
  isMustChangePasswordPending(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(AuthService.MUST_CHANGE_PASSWORD_KEY) === '1'
    );
  }

  /** Limpia la marca de cambio de contraseña vencida (tras un cambio exitoso). */
  clearMustChangePassword(): void {
    this.setMustChangePassword(false);
  }

  clearAllData(): void {
    this.accessTokenValue = null;
    this.refreshTokenValue = null;
    this.currentUser = null;
    this.sessionId = null;
    this.refreshFailureCount = 0;

    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('user_data');
      localStorage.removeItem('hasMultipleModules');
      localStorage.removeItem('selectedProductId');
      localStorage.removeItem('selectedRoute');
      localStorage.removeItem('selectedModule');
      localStorage.removeItem(AuthService.MUST_CHANGE_PASSWORD_KEY);

      sessionStorage.removeItem('hasMultipleModules');
      sessionStorage.removeItem('selectedProductId');
      sessionStorage.removeItem('selectedRoute');
      sessionStorage.removeItem('selectedModule');
      sessionStorage.removeItem('session_expired_shown');
      sessionStorage.removeItem('refresh_count');
      sessionStorage.removeItem('companyName');
    }

    this.cookieService.delete('session_data', '/');
    this.cookieService.delete('user_profile', '/');

    this.cookieService.delete('app_data', '/');
    this.cookieService.delete('app_data', '/', window.location.hostname);
    this.cookieService.delete('app_data', '/', 'localhost');

    this.isAuthenticatedSubject.next(false);
    this.isRefreshingSubject.next(false);
    this.refreshTokenQueue = [];
    this.clearRefreshTimeout();
  }

  private saveSafeUserDataToCookie(userData: UserData): void {
    const safeUserData = {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      preferredLanguage: userData.preferredLanguage,
      roles: userData.roles.map((r: any) => ({
        id: r.id,
        name: r.name,
        authority: r.authority,
      })),
      companyId: userData.companyId,
      customerId: userData.customerId,
    };

    const baseDomain = window.location.hostname;
    const expireAfter = new Date();
    expireAfter.setDate(expireAfter.getDate() + 2);

    this.cookieService.set('user_profile', JSON.stringify(safeUserData), {
      expires: expireAfter,
      path: '/',
      domain: baseDomain,
      secure: true,
      sameSite: 'Strict',
    });
  }

  private extractSafeUserData(userData: UserData): SafeUserData {
    return {
      id: userData.id,
      username: userData.username,
      email: userData.email,
      preferredLanguage: userData.preferredLanguage,
      roles: userData.roles.map((r: any) => ({
        id: r.id,
        name: r.name,
        authority: r.authority,
      })),
      companyId: userData.companyId,
      customerId: userData.customerId,
    };
  }

  public getSafeUserData(): SafeUserData | null {
    try {
      const cookieData = this.cookieService.get('user_profile');
      if (cookieData) {
        return JSON.parse(cookieData);
      }
    } catch {
      return null;
    }
    return null;
  }

  /**
   * Rehace una petición con el token indicado en las dos cabeceras que espera el ecosistema.
   *
   * @param req petición original
   * @param token access token a aplicar
   * @return la petición clonada, lista para reintentar
   */
  private conToken<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`).set('AccessToken', token),
    });
  }

  /**
   * Reintenta la petición cuando el refresco acaba de ocurrir y el token en mano ya no es el que
   * provocó el 401.
   *
   * @param req petición que falló
   * @param next siguiente eslabón de la cadena
   * @return el reintento, o `null` si el token no cambió y toca refrescar
   */
  private reintentarConTokenVigente<T>(
    req: HttpRequest<T>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<T>> | null {
    const currentToken = this.getToken();

    if (!currentToken || req.headers.get('Authorization') === `Bearer ${currentToken}`) {
      return null;
    }

    return next(this.conToken(req, currentToken)) as unknown as Observable<HttpEvent<T>>;
  }

  public handle401Error<T = any>(
    req: HttpRequest<T>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<T>> {
    if (req.url.includes('/refresh')) {
      const result$ = next(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401 || error.status === 403) {
            this._forceLogout(true);
          }
          return throwError(() => error);
        }),
      );
      return result$ as unknown as Observable<HttpEvent<T>>;
    }

    const now = Date.now();
    const isDebounced = now - this.lastRefreshTimestamp < this.REFRESH_DEBOUNCE_MS;

    // Solo se encola si hay un refresco realmente en vuelo, porque la cola la vacía quien lo
    // termina. Encolar durante el debounce, sin nadie que fuera a vaciarla, dejaba la petición
    // esperando indefinidamente.
    if (this.isRefreshingLock) {
      return defer(
        () =>
          new Observable<HttpEvent<T>>((observer) => {
            this.refreshTokenQueue.push((newToken: string) => {
              next(this.conToken(req, newToken)).subscribe({
                next: (event) => observer.next(event as HttpEvent<T>),
                error: (err) => observer.error(err),
                complete: () => observer.complete(),
              });
            });
          }),
      );
    }

    // Se acaba de refrescar: el token vigente ya no es el que provocó este 401, así que la
    // petición se reintenta con él en vez de disparar otro refresco. Si el token no cambió, se
    // sigue al camino normal, que es el que sabe reintentar o cerrar la sesión.
    if (isDebounced) {
      const reintento$ = this.reintentarConTokenVigente(req, next);
      if (reintento$) {
        return reintento$;
      }
    }

    this.isRefreshingLock = true;
    this.lastRefreshTimestamp = now;
    this.isRefreshingSubject.next(true);

    const refreshResult$ = this.refreshToken().pipe(
      switchMap((content: AuthContent) => {
        this.isRefreshingLock = false;
        this.isRefreshingSubject.next(false);

        const queueLength = this.refreshTokenQueue.length;
        if (queueLength > 0) {
          const queue = [...this.refreshTokenQueue];
          this.refreshTokenQueue = [];
          queue.forEach((callback) => {
            callback(content.accessToken);
          });
        }

        this.scheduleTokenRefresh();

        return next(this.conToken(req, content.accessToken));
      }),
      catchError((error: HttpErrorResponse) => {
        this.isRefreshingLock = false;
        this.isRefreshingSubject.next(false);
        this.refreshTokenQueue = [];

        if (error.status === 401 || error.status === 403) {
          this._forceLogout(true);
        }

        return throwError(() => error);
      }),
    );

    return refreshResult$ as unknown as Observable<HttpEvent<T>>;
  }

  private scheduleTokenRefresh(): void {
    this.clearRefreshTimeout();

    const expiryTime = this.getTokenExpiry();
    if (!expiryTime) return;

    const timeUntilExpiry = expiryTime - Date.now();

    // Un token ya vencido daba `timeUntilExpiry` negativo y la condición previa descartaba el
    // temporizador, así que la sesión quedaba muerta en silencio hasta que alguna petición
    // tropezara con un 401. Ocurre siempre que el equipo se suspende o la pestaña pasa a segundo
    // plano y el navegador estira el temporizador anterior. Ahora se refresca cuanto antes.
    const refreshTime = Math.max(timeUntilExpiry - 30000, 1000);

    this.refreshTokenTimeout = setTimeout(() => {
      this.refreshToken().subscribe({
        error: () => {
          /* El fallo ya lo resuelve refreshToken: reintenta o fuerza el cierre de sesión. */
        },
      });
    }, refreshTime);
  }

  private clearRefreshTimeout(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }

  public getToken(): string | null {
    return (
      this.accessTokenValue ||
      (typeof window !== 'undefined' ? localStorage.getItem('access_token') : null)
    );
  }

  public getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;

    const storageToken = localStorage.getItem('refresh_token');

    this.refreshTokenValue = storageToken;

    return storageToken;
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public getCurrentUser(): UserData | null {
    return this.currentUser;
  }

  public getUserRoles() {
    return this.currentUser?.roles || this.getSafeUserData()?.roles || [];
  }

  public getUserPermissions() {
    const roles = this.currentUser?.roles || this.getSafeUserData()?.roles || [];
    return roles.map((r: any) => r.authority);
  }

  public getCompanyId(): number | null {
    return this.currentUser?.companyId || this.getSafeUserData()?.companyId || null;
  }

  public getCustomerId(): number | null {
    return this.currentUser?.customerId || this.getSafeUserData()?.customerId || null;
  }

  public getEnvironment(): string | null {
    return this.currentUser?.environment || null;
  }

  public getPreferredLanguage(): string | null {
    return this.currentUser?.preferredLanguage || this.getSafeUserData()?.preferredLanguage || null;
  }

  /**
   * Moneda con la que se muestran los importes en la sesion.
   *
   * El token de seleccion de compania resuelve dos: la preferida del usuario y
   * la base de la compania. Manda la preferida, porque es la que el usuario
   * eligio para ver; la base queda de respaldo cuando no hay preferencia.
   *
   * @returns el codigo ISO (p. ej. 'COP'), o null si el token no lo trae
   */
  public getSessionCurrency(): string | null {
    return this.currentUser?.preferredCurrency || this.currentUser?.companyBaseCurrency || null;
  }

  public isCompanyLocked(): boolean {
    return this.currentUser?.companyLocked || false;
  }

  public isLegacyUser(): boolean {
    return this.currentUser?.legacySystemUser || false;
  }

  public getConnectionContext(): string | null {
    return this.currentUser?.connectionContext || null;
  }

  public getTokenExpiry(): number | null {
    if (typeof window === 'undefined') return null;
    const expiry = localStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry, 10) : null;
  }

  public hasRole(roleName: string): boolean {
    const roles = this.currentUser?.roles || this.getSafeUserData()?.roles || [];
    return roles.some((r: any) => r.name === roleName || r.authority === roleName);
  }

  public hasAllRoles(roleNames: string[]): boolean {
    return roleNames.every((role) => this.hasRole(role));
  }

  private hasValidToken(): boolean {
    if (typeof window === 'undefined') return false;

    const token = this.getToken();
    const expiry = this.getTokenExpiry();

    if (!token || !expiry) return false;

    return Date.now() < expiry - 30000;
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  /**
   * Indica si el access token en mano sigue vigente.
   *
   * Es una consulta, no una decisión: no borra nada. Antes limpiaba la sesión al responder que
   * no, y con ello se llevaba por delante el refresh token, que suele seguir siendo válido —el
   * access vence mucho antes—. Quien decide expulsar es quien limpia: el guard tras agotar el
   * refresco, o {@link _forceLogout}.
   *
   * @return `true` si el token está vigente; `false` si venció o está dentro del margen previo.
   */
  public isSessionActive(): boolean {
    return this.hasValidToken();
  }

  public validateTokenAndGetSession(): Observable<UserData> {
    const token = this.getToken();

    if (!token) {
      return throwError(() => new Error('No hay token disponible'));
    }

    const headers = new HttpHeaders({
      AccessToken: token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });

    const endpoint = `${this.AUTH_ENDPOINT}/jwt-get`;

    return this.http.post<UserData>(endpoint, {}, undefined, headers).pipe(
      map((response: ApiResponse<UserData>) => {
        return response.response.content;
      }),
      tap((userData: UserData) => {
        this.currentUser = userData;
        this.sessionId = userData.sessionId;
        this.saveSafeUserDataToCookie(userData);
        localStorage.setItem('user_data', JSON.stringify(this.extractSafeUserData(userData)));
      }),
      catchError((error: unknown) => {
        return throwError(() => error);
      }),
    );
  }

  public validateTokenOnly(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(false);
    }

    return this.http
      .post<boolean>(
        `${this.AUTH_ENDPOINT}/jwt`,
        {},
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          }),
        },
      )
      .pipe(
        map((response: ApiResponse<boolean>) => response.response.content),
        catchError(() => {
          return of(false);
        }),
      );
  }

  public updateTokensAfterCompanySelection(
    accessToken: string,
    refreshToken: string | undefined,
    expiresIn: number | undefined,
  ): void {
    this.accessTokenValue = accessToken;
    if (refreshToken) this.refreshTokenValue = refreshToken;

    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      if (expiresIn) {
        const expiryTime = expiresIn > 1e12 ? expiresIn : Date.now() + expiresIn;
        localStorage.setItem('token_expiry', expiryTime.toString());
      }
    }

    const decoded = this.decodeToken(accessToken);
    if (decoded?.User) {
      this.currentUser = decoded.User;
      this.sessionId = decoded.User.sessionId;
      this.saveSafeUserDataToCookie(decoded.User);
      localStorage.setItem('user_data', JSON.stringify(this.extractSafeUserData(decoded.User)));
    }

    this.isAuthenticatedSubject.next(true);
    this.scheduleTokenRefresh();

    if (this.tokenBridge) {
      this.tokenBridge.updateCookie();
    }
  }
}
